/**
 * AutoGlow · Advisor API · Sprint T-13.
 *
 * CENTRAL data layer for the Advisor Dashboard. Every HTTP call to AG-01..AG-06
 * goes through this module. The frontend NEVER generates intelligence ·
 * it consumes it from the backend. NO calculations · NO aggregations · NO
 * scoring · NO privacy logic (all of that lives in the backend).
 *
 * Endpoint signatures match `/app/backend/routers/ag0X_*.py` 1:1.
 */
import { api } from "../lib/api";

// ---------------------------------------------------------------------------
// AG-01 · Business Advisor · org-based
// ---------------------------------------------------------------------------

export const businessApi = {
  diagnosis: (org_id) => api.get(`/ai/business/diagnosis`, { params: { org_id } }),
  score: (org_id, competitor_id) =>
    api.get(`/ai/business/score`, { params: { org_id, competitor_id } }),
  recommendations: (org_id, competitor_id) =>
    api.get(`/ai/business/recommendations`, { params: { org_id, competitor_id } }),
  priorities: (org_id, competitor_id) =>
    api.get(`/ai/business/priorities`, { params: { org_id, competitor_id } }),
  roadmap: (org_id) => api.get(`/ai/business/roadmap`, { params: { org_id } }),
  summary: (org_id, with_narrative = false) =>
    api.get(`/ai/business/summary`, { params: { org_id, with_narrative } }),
  report: (org_id, with_narrative = false, competitor_id) =>
    api.get(`/ai/business/report`, {
      params: { org_id, with_narrative, competitor_id },
    }),
};

// ---------------------------------------------------------------------------
// AG-02 · Growth Advisor · org-based
// ---------------------------------------------------------------------------

export const growthApi = {
  info: () => api.get(`/ai/growth/info`),
  funnel: (org_id) => api.get(`/ai/growth/funnel`, { params: { org_id } }),
  experiments: (org_id, competitor_id) =>
    api.get(`/ai/growth/experiments`, { params: { org_id, competitor_id } }),
  plan: (org_id, horizon = "monthly") =>
    api.get(`/ai/growth/plan`, { params: { org_id, horizon } }),
  kpis: (org_id) => api.get(`/ai/growth/kpis`, { params: { org_id } }),
  kpi: (org_id, kpi_code) =>
    api.get(`/ai/growth/kpi/${encodeURIComponent(kpi_code)}`, {
      params: { org_id },
    }),
  forecast: (org_id, horizon = "monthly") =>
    api.get(`/ai/growth/forecast`, { params: { org_id, horizon } }),
  summary: (org_id, with_narrative = false) =>
    api.get(`/ai/growth/summary`, { params: { org_id, with_narrative } }),
  report: (org_id, opts = {}) =>
    api.get(`/ai/growth/report`, {
      params: {
        org_id,
        competitor_id: opts.competitor_id,
        horizon: opts.horizon || "monthly",
        with_narrative: !!opts.with_narrative,
      },
    }),
};

// ---------------------------------------------------------------------------
// AG-03 · SEO Strategist · CODE-based (NO org_id · the engine is per taxonomy node)
// ---------------------------------------------------------------------------

export const seoApi = {
  info: () => api.get(`/ai/seo/info`),
  brief: (code) => api.get(`/ai/seo/brief`, { params: { code } }),
  cluster: (code) => api.get(`/ai/seo/cluster`, { params: { code } }),
  links: (code) => api.get(`/ai/seo/links`, { params: { code } }),
  schema: (code) => api.get(`/ai/seo/schema`, { params: { code } }),
  score: (code) => api.get(`/ai/seo/score`, { params: { code } }),
  fullReport: (code) => api.get(`/ai/seo/report/${encodeURIComponent(code)}`),
};

// ---------------------------------------------------------------------------
// AG-04 · Content Intelligence · CODE-based read-only previews
// ---------------------------------------------------------------------------

export const contentApi = {
  info: () => api.get(`/ai/content/info`),
  preview: (code, content_type = "landing") =>
    api.get(`/ai/content/preview`, { params: { code, content_type } }),
  types: () => api.get(`/ai/content/types`),
};

// ---------------------------------------------------------------------------
// AG-05 · Competitor Intelligence · backend uses focal/competitor params
// ---------------------------------------------------------------------------

export const competitorApi = {
  info: () => api.get(`/ai/competitor/info`),
  profile: (org_id) => api.get(`/ai/competitor/profile`, { params: { org_id } }),
  candidates: (org_id, limit = 10) =>
    api.get(`/ai/competitor/candidates`, { params: { org_id, limit } }),
  gap: (focal, competitor) =>
    api.get(`/ai/competitor/gap`, { params: { focal, competitor } }),
  swot: (org_id, competitor_id) =>
    api.get(`/ai/competitor/swot`, { params: { org_id, competitor_id } }),
  opportunities: (org_id, limit = 20) =>
    api.get(`/ai/competitor/opportunities`, { params: { org_id, limit } }),
  market: (org_id, limit = 15) =>
    api.get(`/ai/competitor/market`, { params: { org_id, limit } }),
  report: (focal, competitor) =>
    api.get(`/ai/competitor/report`, { params: { focal, competitor } }),
};

// ---------------------------------------------------------------------------
// AG-06 · Marketplace Intelligence · dual-scope (founder full · client sanitized)
// ---------------------------------------------------------------------------

export const marketplaceApi = {
  info: () => api.get(`/ai/marketplace/info`),
  ecosystemOverview: () => api.get(`/ai/marketplace/ecosystem_overview`),
  sectorIntelligence: (sector_code) =>
    api.get(`/ai/marketplace/sector_intelligence`, { params: { sector_code } }),
  geoIntelligence: (city) =>
    api.get(`/ai/marketplace/geo_intelligence`, { params: { city } }),
  leaderboard: (metric = "funnel_health", limit = 10) =>
    api.get(`/ai/marketplace/leaderboard`, { params: { metric, limit } }),
  benchmark: (org_id, metric = "funnel_health") =>
    api.get(`/ai/marketplace/benchmark`, { params: { org_id, metric } }),
  demandSupplyGap: () => api.get(`/ai/marketplace/demand_supply_gap`),
  trends: (horizon_days = 30) =>
    api.get(`/ai/marketplace/trends`, { params: { horizon_days } }),
  report: (opts = {}) =>
    api.get(`/ai/marketplace/report`, {
      params: {
        org_id: opts.org_id,
        sector_code: opts.sector_code,
        city: opts.city,
        metric: opts.metric || "funnel_health",
      },
    }),
};

// ---------------------------------------------------------------------------
// PUBLIC org resolver (needed to convert /centro/advisor/{slug} into an org_id
// for AG-01..AG-06 calls). Reuses an existing public endpoint · NO new API.
// ---------------------------------------------------------------------------

export const orgApi = {
  publicBySlug: (slug) => api.get(`/public/org/${encodeURIComponent(slug)}`),
  /** Marketplace public listing · also surfaces taxonomy_codes for founder picker UX. */
  marketplaceList: (limit = 50) =>
    api.get(`/marketplace/orgs`, { params: { limit } }),
};

// Aggregate · convenience namespace export
export const advisorApi = {
  business: businessApi,
  growth: growthApi,
  seo: seoApi,
  content: contentApi,
  competitor: competitorApi,
  marketplace: marketplaceApi,
  org: orgApi,
};

export default advisorApi;
